import { DialogComponent } from '@theme/dialog';
import { clamp } from '@theme/utilities';

const ZOOM_STEP = 0.5;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

/**
 * Fullscreen product image viewer - large image, prev/next arrows, zoom in/out and a
 * thumbnail strip. Mirrors the live site's Fotorama fullscreen gallery.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLImageElement} image
 *
 * @extends DialogComponent<Refs>
 */
export class KawProductLightbox extends DialogComponent {
  requiredRefs = ['dialog', 'image'];

  #index = 0;
  #scale = ZOOM_MIN;
  #pan = { x: 0, y: 0 };
  #dragStart = null;

  /** @type {HTMLElement[]} */
  #thumbs = [];

  connectedCallback() {
    super.connectedCallback();
    this.#thumbs = Array.from(this.querySelectorAll('[data-full]'));
    this.refs.image.addEventListener('pointerdown', this.#handlePointerDown);
    this.refs.image.addEventListener('pointermove', this.#handlePointerMove);
    this.refs.image.addEventListener('pointerup', this.#handlePointerUp);
    this.refs.image.addEventListener('pointercancel', this.#handlePointerUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.image.removeEventListener('pointerdown', this.#handlePointerDown);
    this.refs.image.removeEventListener('pointermove', this.#handlePointerMove);
    this.refs.image.removeEventListener('pointerup', this.#handlePointerUp);
    this.refs.image.removeEventListener('pointercancel', this.#handlePointerUp);
  }

  /**
   * Opens the lightbox on a given image.
   * @param {number} index
   */
  open(index) {
    this.#index = index || 0;
    this.#render();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  next() {
    this.#step(1);
  }

  prev() {
    this.#step(-1);
  }

  /** @param {number} index */
  goTo(index) {
    this.#index = index;
    this.#render();
  }

  zoomIn() {
    this.#setScale(this.#scale + ZOOM_STEP);
  }

  zoomOut() {
    this.#setScale(this.#scale - ZOOM_STEP);
  }

  /**
   * Arrow-key nav while the dialog is open - Escape is already handled by DialogComponent.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowRight') this.next();
    if (event.key === 'ArrowLeft') this.prev();
  }

  /** @param {number} delta */
  #step(delta) {
    const nextIndex = this.#index + delta;
    if (nextIndex < 0 || nextIndex >= this.#thumbs.length) return;
    this.#index = nextIndex;
    this.#render();
  }

  #render() {
    const thumb = this.#thumbs[this.#index];
    if (!thumb) return;
    this.refs.image.src = thumb.dataset.full ?? '';
    this.refs.image.alt = thumb.dataset.alt ?? '';
    this.#thumbs.forEach((t, i) => t.classList.toggle('kaw-lightbox__thumb--active', i === this.#index));
    this.#setScale(ZOOM_MIN);
  }

  /** @param {number} scale */
  #setScale(scale) {
    this.#scale = clamp(scale, ZOOM_MIN, ZOOM_MAX);
    this.#pan = { x: 0, y: 0 };
    this.#applyTransform();
  }

  #applyTransform() {
    const { image } = this.refs;
    image.style.transform = `translate(${this.#pan.x}px, ${this.#pan.y}px) scale(${this.#scale})`;
    image.classList.toggle('kaw-lightbox__image--zoomed', this.#scale > ZOOM_MIN);
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (this.#scale <= ZOOM_MIN) return;
    this.#dragStart = { x: event.clientX - this.#pan.x, y: event.clientY - this.#pan.y };
    this.refs.image.setPointerCapture(event.pointerId);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!this.#dragStart) return;
    this.#pan = { x: event.clientX - this.#dragStart.x, y: event.clientY - this.#dragStart.y };
    this.#applyTransform();
  };

  #handlePointerUp = () => {
    this.#dragStart = null;
  };
}

if (!customElements.get('kaw-product-lightbox')) {
  customElements.define('kaw-product-lightbox', KawProductLightbox);
}
